/**
 * When does resolving a proposal card resume the meta-agent's turn?
 *
 * Extracted from AoAChatPanel for the same reason batchSettling was: the rule
 * is three lines and the bug in it was invisible, because every way of
 * reaching it goes through a React component and an SSE stream.
 *
 * THE BUG THIS EXISTS FOR. `auto_continue` is computed by the backend and
 * attached to the RESOLVE response only (routes.py). Several paths in the UI
 * reconcile a card that someone else already resolved — the auto-accept fast
 * path racing its own sweep, the inspector's card, a second tab — and those
 * paths re-read the proposal with `GET /proposals/<id>`, whose body is a plain
 * `to_dict()` with no such field. Reading that absent field as "do not
 * continue" left the card showing "applied" while the agent said nothing
 * further, which is the stall users describe as the card getting stuck. It is
 * intermittent because it needs the race.
 *
 * So: absent means UNKNOWN and is derived from the status; an explicit false
 * (the chat's own `silent` bookkeeping reject, where the user's new message is
 * already driving the turn) still wins.
 *
 * The second half is idempotency. Once absent-means-derive is in, MORE paths
 * legitimately ask for a continuation than before, and two asks for one
 * decision would make the agent answer twice. `alreadyContinued` is the claim
 * check: whoever asks first wins and every later report of the same decision
 * is a no-op, which is what lets each individual path stay liberal about
 * asking instead of staying silent just in case.
 */

/** The statuses that MEAN the user answered the card. Mirrors `auto_continue`
 *  in routes.py: accepting, editing AND declining are all answers to it. */
export const CONTINUABLE_STATUSES: ReadonlySet<string> = new Set([
  'approved',
  'edited',
  'rejected',
]);

export interface ResolvedProposalLike {
  proposal_id: string;
  status: string;
  /** Present only on a resolve response; absent on any GET-read proposal. */
  auto_continue?: boolean;
}

/**
 * Should this resolution open a continuation stream?
 *
 * @param resolved         the proposal as reported by whichever path saw it
 * @param alreadyContinued proposal ids this panel has already continued from
 */
export function shouldContinueFrom(
  resolved: ResolvedProposalLike,
  alreadyContinued: ReadonlySet<string>,
): boolean {
  if (alreadyContinued.has(resolved.proposal_id)) return false;
  return resolved.auto_continue ?? CONTINUABLE_STATUSES.has(resolved.status);
}

export default shouldContinueFrom;
